import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';
import { INVENTORY_GROUPS, aliasesForCategory, labelForCategory } from '../../constants/inventoryTemplates';

function categoryMatches(category, needle) {
  if (!needle) return true;
  if (category.label.toLowerCase().includes(needle)) return true;
  return aliasesForCategory(category.value).some((alias) => alias.toLowerCase().includes(needle));
}

// Search input row (~44px) + the 320px capped list. A caller that wants the
// page to reserve space for the open panel (rather than letting it float
// over whatever sits below) can use this to size a spacer.
export const CATEGORY_PANEL_RESERVED_HEIGHT = 368;

// Searchable, grouped category picker. Filters every group by label + alias,
// hides groups with no matches, and only shows group headers when they're
// not needed to disambiguate a single-group result (spec: headers show when
// idle, or once a filter spans 2+ groups).
export default function CategoryCombobox({
  value,
  onChange,
  onAfterSelect,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  placeholder = 'Select category',
  triggerClassName,
  triggerTestId,
}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = onOpenChangeProp || setOpenState;
  const [search, setSearch] = useState('');

  const filteredGroups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return INVENTORY_GROUPS
      .map((group) => ({
        header: group.header,
        categories: group.categories.filter((c) => categoryMatches(c, needle)),
      }))
      .filter((group) => group.categories.length > 0);
  }, [search]);

  const isFiltering = search.trim().length > 0;
  const showHeaders = !isFiltering || filteredGroups.length >= 2;

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const handleSelect = (categoryValue) => {
    onChange(categoryValue);
    setOpen(false);
    setSearch('');
    // Radix restores focus to the trigger on close; defer past that so the
    // model field ends up focused instead.
    requestAnimationFrame(() => onAfterSelect?.());
  };

  const currentLabel = value ? labelForCategory(value) : '';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={triggerTestId}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-white/[0.08] bg-[#151515] px-3 text-sm text-white',
            !currentLabel && 'text-white/40',
            triggerClassName
          )}
        >
          <span className="truncate">{currentLabel || placeholder}</span>
          <ChevronDown size={14} className="ml-2 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] overflow-hidden p-0"
        style={{ maxHeight: 'min(320px, var(--radix-popover-content-available-height, 320px))' }}
      >
        <Command shouldFilter={false} className="flex h-full flex-col overflow-hidden">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search categories..."
            data-testid={triggerTestId ? `${triggerTestId}-search` : undefined}
          />
          <CommandList className="max-h-none flex-1 overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-[var(--t2)]">No matches</CommandEmpty>
            {filteredGroups.map((group) => (
              <CommandGroup
                key={group.header}
                heading={showHeaders ? group.header : undefined}
                className={cn(
                  '[&_[cmdk-group-heading]]:text-[length:var(--s8-fs-label)]',
                  '[&_[cmdk-group-heading]]:tracking-[var(--s8-tracking-label)]',
                  '[&_[cmdk-group-heading]]:uppercase',
                  '[&_[cmdk-group-heading]]:text-[var(--t3)]'
                )}
              >
                {group.categories.map((category) => (
                  <CommandItem
                    key={category.value}
                    value={category.value}
                    onSelect={() => handleSelect(category.value)}
                    className="text-[var(--t1)]"
                  >
                    {category.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
